
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Bug, RefreshCw, Download, Filter, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';

interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warn' | 'info';
  message: string;
  source: 'database' | 'edge-function' | 'auth' | 'application';
  context?: string;
  stack_trace?: string;
  resolved: boolean;
}

interface ErrorStats {
  total: number;
  database: number;
  edgeFunction: number;
  auth: number;
  application: number;
  critical: number;
}

export const SystemErrors = () => {
  const [timeFilter, setTimeFilter] = useState('24h');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showResolved, setShowResolved] = useState(false);
  const [expandedError, setExpandedError] = useState<string | null>(null);

  // Mock data - in real implementation, this would fetch from Supabase analytics
  const mockErrors: ErrorLog[] = [
    {
      id: '1',
      timestamp: new Date().toISOString(),
      level: 'error',
      message: 'invalid input syntax for type uuid: "null"',
      source: 'database',
      context: 'support_chats table query',
      stack_trace: 'ERROR: invalid input syntax for type uuid: "null"\nLINE 1: ...WHERE user_id = null',
      resolved: false
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      level: 'error',
      message: 'Function timeout after 30s',
      source: 'edge-function',
      context: 'generate-listing-description',
      resolved: false
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      level: 'warn',
      message: 'Authentication token expired',
      source: 'auth',
      context: 'User session validation',
      resolved: true
    },
    {
      id: '4',
      timestamp: new Date(Date.now() - 10800000).toISOString(),
      level: 'error',
      message: 'Failed to load user profile',
      source: 'application',
      context: 'AdminChats component',
      resolved: false
    }
  ];

  const mockStats: ErrorStats = {
    total: 156,
    database: 89,
    edgeFunction: 34,
    auth: 23,
    application: 10,
    critical: 12
  };

  const { data: errors = mockErrors, isLoading, refetch } = useQuery({
    queryKey: ['systemErrors', timeFilter, sourceFilter, showResolved],
    queryFn: async () => {
      // In real implementation, fetch from Supabase analytics API
      // For now, return mock data filtered by our parameters
      let filtered = mockErrors;
      
      if (sourceFilter !== 'all') {
        filtered = filtered.filter(error => error.source === sourceFilter);
      }
      
      if (!showResolved) {
        filtered = filtered.filter(error => !error.resolved);
      }
      
      // Apply time filter
      const now = Date.now();
      const timeMs = timeFilter === '24h' ? 24 * 60 * 60 * 1000 :
                    timeFilter === '7d' ? 7 * 24 * 60 * 60 * 1000 :
                    30 * 24 * 60 * 60 * 1000;
      
      filtered = filtered.filter(error => 
        now - new Date(error.timestamp).getTime() <= timeMs
      );
      
      return filtered;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'database': return '🗄️';
      case 'edge-function': return '⚡';
      case 'auth': return '🔐';
      case 'application': return '💻';
      default: return '❓';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'database': return 'bg-red-100 text-red-800';
      case 'edge-function': return 'bg-yellow-100 text-yellow-800';
      case 'auth': return 'bg-blue-100 text-blue-800';
      case 'application': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'bg-red-500';
      case 'warn': return 'bg-yellow-500';
      case 'info': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const markAsResolved = async (errorId: string) => {
    // In real implementation, update error status in database
    console.log('Marking error as resolved:', errorId);
    refetch();
  };

  const exportErrors = () => {
    const csv = errors.map(error => 
      `"${error.timestamp}","${error.level}","${error.source}","${error.message.replace(/"/g, '""')}","${error.resolved}"`
    ).join('\n');
    const blob = new Blob([`Timestamp,Level,Source,Message,Resolved\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-errors-${timeFilter}.csv`;
    a.click();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              System Fehlerprotokoll
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Überwachung und Analyse von Systemfehlern ({errors.length} aktive Fehler)
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Aktualisieren
            </Button>
            <Button variant="outline" size="sm" onClick={exportErrors}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error Statistics Overview */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="p-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{mockStats.total}</div>
              <div className="text-xs text-gray-600">Gesamt</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">{mockStats.database}</div>
              <div className="text-xs text-gray-600">Datenbank</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-500">{mockStats.edgeFunction}</div>
              <div className="text-xs text-gray-600">Functions</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">{mockStats.auth}</div>
              <div className="text-xs text-gray-600">Auth</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-500">{mockStats.application}</div>
              <div className="text-xs text-gray-600">App</div>
            </div>
          </Card>
          <Card className="p-3 border-red-200">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-700">{mockStats.critical}</div>
              <div className="text-xs text-red-600">Kritisch</div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">Filter:</span>
          </div>
          
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Letzte 24h</SelectItem>
              <SelectItem value="7d">Letzte 7 Tage</SelectItem>
              <SelectItem value="30d">Letzte 30 Tage</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Quellen</SelectItem>
              <SelectItem value="database">Datenbank</SelectItem>
              <SelectItem value="edge-function">Edge Functions</SelectItem>
              <SelectItem value="auth">Authentication</SelectItem>
              <SelectItem value="application">Application</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant={showResolved ? "default" : "outline"}
            size="sm"
            onClick={() => setShowResolved(!showResolved)}
          >
            {showResolved ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
            {showResolved ? 'Alle anzeigen' : 'Gelöste anzeigen'}
          </Button>
        </div>

        {/* Error List */}
        <Tabs defaultValue="recent" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="recent">Aktuelle Fehler</TabsTrigger>
            <TabsTrigger value="trends">Trends & Analyse</TabsTrigger>
          </TabsList>
          
          <TabsContent value="recent">
            <div className="space-y-3">
              {isLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Lade Fehlerprotokoll...</p>
                </div>
              ) : errors.length === 0 ? (
                <div className="text-center py-8">
                  <Bug className="h-8 w-8 mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Keine Fehler gefunden</p>
                </div>
              ) : (
                errors.map((error) => (
                  <Card key={error.id} className={`border-l-4 ${getLevelColor(error.level)} ${error.resolved ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{getSourceIcon(error.source)}</span>
                            <Badge className={getSourceColor(error.source)}>
                              {error.source}
                            </Badge>
                            <Badge variant={error.level === 'error' ? 'destructive' : error.level === 'warn' ? 'default' : 'secondary'}>
                              {error.level.toUpperCase()}
                            </Badge>
                            {error.resolved && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Gelöst
                              </Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {format(new Date(error.timestamp), 'dd.MM.yyyy HH:mm:ss')}
                            </span>
                          </div>
                          
                          <p className="font-medium text-gray-900 mb-1">{error.message}</p>
                          
                          {error.context && (
                            <p className="text-sm text-gray-600">Kontext: {error.context}</p>
                          )}
                          
                          {expandedError === error.id && error.stack_trace && (
                            <div className="mt-3 p-3 bg-gray-50 rounded border text-xs font-mono">
                              <pre className="whitespace-pre-wrap">{error.stack_trace}</pre>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          {error.stack_trace && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedError(expandedError === error.id ? null : error.id)}
                            >
                              {expandedError === error.id ? 'Weniger' : 'Details'}
                            </Button>
                          )}
                          {!error.resolved && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markAsResolved(error.id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              Als gelöst markieren
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="trends">
            <div className="space-y-4">
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4">Häufigste Fehlertypen</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">UUID Syntax Fehler</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                      <span className="text-sm font-medium">78%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Function Timeouts</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '15%' }}></div>
                      </div>
                      <span className="text-sm font-medium">15%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Auth Token Expired</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '7%' }}></div>
                      </div>
                      <span className="text-sm font-medium">7%</span>
                    </div>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4">Empfohlene Maßnahmen</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <h4 className="font-medium text-red-800">Kritisch: UUID Validierung</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Frontend sendet häufig null-Werte statt gültiger UUIDs. 
                      Implementiere UUID-Validierung vor Datenbankabfragen.
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <h4 className="font-medium text-yellow-800">Warnung: Function Performance</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Edge Functions überschreiten 30s Timeout. 
                      Optimiere AI-Anfragen oder implementiere Async-Processing.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
